import { deletePushSubscription, getPushConfig, savePushSubscription } from "@/lib/api";

function applicationServerKey(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const raw = atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
}

export function supportsWebPush() {
  return window.isSecureContext && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function currentPushState() {
  if (!supportsWebPush()) return { supported: false, subscribed: false, permission: "unsupported" };
  const registration = await navigator.serviceWorker.register("/service-worker.js");
  const subscription = await registration.pushManager.getSubscription();
  return { supported: true, subscribed: Boolean(subscription), permission: Notification.permission };
}

export async function enableWebPush() {
  if (!supportsWebPush()) throw new Error("Push notifications are not supported in this browser or connection.");
  const config = await getPushConfig();
  if (!config.enabled || !config.publicKey) throw new Error("Device notifications are not configured on the server yet.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notification permission was not granted.");
  const registration = await navigator.serviceWorker.register("/service-worker.js");
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey(config.publicKey),
    });
  }
  const payload = subscription.toJSON();
  await savePushSubscription({ ...payload, userAgent: navigator.userAgent });
  return { supported: true, subscribed: true, permission };
}

// Keeps the server's copy of this browser's subscription current. The browser can replace a
// subscription, and the server drops an endpoint that bounces; without this the device would
// go quiet until the person turned notifications off and on again. Runs when the app shell loads.
export async function syncWebPush() {
  if (!supportsWebPush() || Notification.permission !== "granted") return;
  const registration = await navigator.serviceWorker.register("/service-worker.js");
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) await savePushSubscription({ ...subscription.toJSON(), userAgent: navigator.userAgent });
}

export async function disableWebPush() {
  if (!supportsWebPush()) return { supported: false, subscribed: false, permission: "unsupported" };
  const registration = await navigator.serviceWorker.getRegistration("/");
  const subscription = await registration?.pushManager.getSubscription();
  if (subscription) {
    await deletePushSubscription(subscription.endpoint);
    await subscription.unsubscribe();
  }
  return { supported: true, subscribed: false, permission: Notification.permission };
}
